import React, { useCallback, useEffect, useState } from "react";
import { CircularProgress, Box } from "@mui/material";
import { useLocation } from "react-router-dom";
import PageErrorBoundary from "../components/PageErrorBoundary";
import DigitalPayLayout from "./digital-pay/DigitalPayLayout";
import DashboardPanel from "./digital-pay/DashboardPanel";
import CardsPanel from "./digital-pay/CardsPanel";
import PersonalPanel from "./digital-pay/PersonalPanel";
import PaymentsPanel from "./digital-pay/PaymentsPanel";
import TransactionsPanel from "./digital-pay/TransactionsPanel";
import FormListPanel from "./digital-pay/FormListPanel";
import { ENTITY_BY_PATH, buildEntityConfig } from "./digital-pay/entities";
import { getFirms, getDepartments, getAuthCompanies } from "../api/digitalPayAPI";

// Vendai Digital Pay'in tek sayfası. Sidebar sabit kalır, route hangi paneli
// göstereceğimizi söyler. Referans listeler (firma/departman/çatı şirket)
// bir kez çekilip tüm panellere verilir — her panel ayrı ayrı çekmesin.
export default function DigitalPay() {
  const location = useLocation();
  const [lookups, setLookups] = useState(null);

  const loadLookups = useCallback(async () => {
    const [firmsResult, departmentsResult, authCompaniesResult] =
      await Promise.allSettled([
        getFirms(),
        getDepartments(),
        getAuthCompanies(),
      ]);

    const resultList = (result) =>
      result.status === "fulfilled" && Array.isArray(result.value)
        ? result.value
        : [];

    const departments = resultList(departmentsResult);
    const authCompanies = resultList(authCompaniesResult);
    const firmsById = new Map(
      resultList(firmsResult).map((firm) => [Number(firm.idfirm), firm])
    );

    // Admin/user için Firm servisi boş veya yetkisiz olabilir. Department cevabındaki
    // firm_idfirm + firmName alanları filtre ve formlar için yeterlidir.
    departments.forEach((department) => {
      const idfirm = Number(department.firm_idfirm);
      if (idfirm && department.firmName && !firmsById.has(idfirm)) {
        firmsById.set(idfirm, { idfirm, name: department.firmName });
      }
    });

    setLookups({
      firms: Array.from(firmsById.values()),
      departments,
      authCompanies,
    });
  }, []);

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  const renderPanel = () => {
    const path = location.pathname;

    // Tanım varlıkları (çatı şirket, firma, departman, limit) ortak deseni sürer.
    const entityKey = ENTITY_BY_PATH[path];
    if (entityKey) {
      const config = buildEntityConfig(entityKey, lookups);
      // key: route değişince form ve liste state'i sıfırdan kurulsun.
      return <FormListPanel key={entityKey} config={config} />;
    }

    switch (path) {
      case "/cards":
        return <CardsPanel lookups={lookups} />;
      case "/personal":
        return <PersonalPanel lookups={lookups} onChanged={loadLookups} />;
      case "/payments":
        return <PaymentsPanel lookups={lookups} />;
      case "/transactions":
        return <TransactionsPanel lookups={lookups} />;
      default:
        return <DashboardPanel />;
    }
  };

  return (
    <DigitalPayLayout>
      <PageErrorBoundary>
        {lookups ? (
          renderPanel()
        ) : (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        )}
      </PageErrorBoundary>
    </DigitalPayLayout>
  );
}
